import React, { ReactNode } from "react";
import styled, { css } from "styled-components";
import { BotData, BotType } from "@shared/types";
import { TV_ALERT_SYMBOL } from "@shared/features/bots/constants.ts";
import NewLookBotStatus from "features/bots/NewLook/NewLookBotStatus";
import {
  getNewLookTokens,
  newLookPhoneType,
  newLookType,
} from "common/NewLook/tokens.ts";
import { useBottomNavigator } from "utils/hooks/misc";
import { NEW_LOOK_DRAWER_CLOSE_HIT } from "common/Drawers/Drawer";
import { BOT_ACTIONS_TRIGGER_ID } from "testIds.ts";
import BotMainIcon from "features/bots/MyBots/common/BotMainIcon";
import { getNewLookMarginText } from "features/bots/NewLook/newLookBotUtils";

interface NewLookBotDrawerTitleProps {
  name?: string;
  botData?: BotData;
  actions?: ReactNode;
}

/**
 * New-look bot drawer header, matching the bot card: icon, name and status;
 * what it trades, where, and its margin underneath; the menu on the right.
 */
const NewLookBotDrawerTitle = ({
  name,
  botData,
  actions,
}: NewLookBotDrawerTitleProps) => {
  const botType = botData?.bot_type;
  const usesGroup =
    botType === BotType.SIGNAL || botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingView =
    botType === BotType.TRADING_VIEW ||
    botType === BotType.TRADING_VIEW_SIGNALS;
  const subject = usesGroup
    ? botData?.sub_group_name
    : botData?.symbols?.[0] || (isTradingView ? TV_ALERT_SYMBOL : undefined);
  const sub = botData
    ? [subject, botData.client_name, getNewLookMarginText(botData)].filter(
        Boolean
      )
    : [];

  const { baseShouldDisplay: isPhone } = useBottomNavigator();

  // Phone: the name gets the whole first line; the status leads the meta
  // line, which keeps to one line with a single ellipsis.
  if (isPhone) {
    const meta = sub.join(" · ");
    return (
      <Wrapper $phone={true}>
        {botData && (!usesGroup || !!botData.sub_group_image) && (
          <BotMainIcon botData={botData} size={36} iconStyle={ICON_STYLE} />
        )}
        <Texts>
          <PhoneName title={name}>{name}</PhoneName>
          {(botData || meta) && (
            <PhoneSub>
              {botData && (
                <PhoneStatus>
                  <NewLookBotStatus bot={botData} />
                </PhoneStatus>
              )}
              {botData && meta && <span aria-hidden={true}>·</span>}
              {meta && <PhoneSubText title={meta}>{meta}</PhoneSubText>}
            </PhoneSub>
          )}
        </Texts>
        {actions && <Actions $phone={true}>{actions}</Actions>}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {botData && (!usesGroup || !!botData.sub_group_image) && (
        <BotMainIcon botData={botData} size={40} iconStyle={ICON_STYLE} />
      )}
      <Texts>
        <NameRow>
          <Name title={name}>{name}</Name>
          {botData && (
            <Status>
              <NewLookBotStatus bot={botData} />
            </Status>
          )}
        </NameRow>
        {sub.length > 0 && (
          <Sub>
            {sub.map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Dot />}
                <SubPart>{part}</SubPart>
              </React.Fragment>
            ))}
          </Sub>
        )}
      </Texts>
      {actions && <Actions>{actions}</Actions>}
    </Wrapper>
  );
};

export default NewLookBotDrawerTitle;

const ICON_STYLE = { marginRight: 0, borderRadius: 12 };

const Wrapper = styled.div<{ $phone?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  padding-right: 36px;
  text-align: left;
  ${(p) =>
    p.$phone &&
    css`
      /* Clears the drawer's close button on the same row. */
      gap: 10px;
      padding-right: ${NEW_LOOK_DRAWER_CLOSE_HIT - 8}px;
    `}
`;
const Texts = styled.div`
  flex: 1;
  min-width: 0;
`;
const NameRow = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;
const Name = styled.div`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 18px;
  line-height: 26px;
  font-weight: 520;
  letter-spacing: -0.01em;
`;
const Status = styled.span`
  flex: none;
  display: inline-flex;
  margin-left: 12px;
`;
// Wraps instead of truncating every part when the drawer is narrow (phone).
const Sub = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 6px;
  min-width: 0;
  margin-top: 2px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const SubPart = styled.span`
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const Dot = styled.span`
  flex: none;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${(p) => getNewLookTokens(p.theme).muted};
  opacity: 0.6;
`;
// On the name row (not the middle of a two-line block), so the menu lines up
// with the drawer's close button.
const Actions = styled.div<{ $phone?: boolean }>`
  flex: none;
  align-self: flex-start;
  height: 26px;
  display: flex;
  align-items: center;
  ${(p) =>
    p.$phone &&
    css`
      /* Centred on the row like the close button, with a 40px target. */
      align-self: center;
      height: ${NEW_LOOK_DRAWER_CLOSE_HIT}px;
      [data-testid="${BOT_ACTIONS_TRIGGER_ID}"] {
        width: ${NEW_LOOK_DRAWER_CLOSE_HIT}px;
        height: ${NEW_LOOK_DRAWER_CLOSE_HIT}px;
        border-radius: 10px;
      }
    `}
`;
const PhoneName = styled.div`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  ${newLookPhoneType.headerTitle};
`;
const PhoneSub = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  margin-top: 2px;
  ${newLookPhoneType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const PhoneStatus = styled.span`
  flex: none;
  display: inline-flex;
  /* The status word sits in the meta line at its size. */
  span:not(:empty) {
    height: 16px;
    font-size: 12px;
    line-height: 16px;
  }
`;
const PhoneSubText = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
